import { 
  Recommendation,
  RecommendationRequest, 
  Game,
  RecommendationAlgorithm
} from '../../types';
import { AdaptiveRecommender } from '../recommenders/adaptiveRecommender';
import { PopularRecommendationService } from './popularRecommendationService';
import { CacheService } from './cacheService';
import { RecommendationStorage } from './recommendationStorage';
import { RecommendationMonitor } from './recommendationMonitor';
import { UserInteractionDataService } from './userInteractionDataService';

export class RecommendationOrchestratorService {
  private cacheExpirationTime = 15 * 60 * 1000; // 15 minutos

  constructor(
    private adaptiveRecommender: AdaptiveRecommender,
    private popularRecommendationService: PopularRecommendationService,
    private cacheService: CacheService,
    private storage: RecommendationStorage,
    private monitor: RecommendationMonitor,
    private userInteractionDataService: UserInteractionDataService
  ) {}

  /**
   * Obtém recomendações para um usuário, orquestrando os diferentes serviços.
   * Retorna um objeto com listas separadas para personalizadas e populares.
   */
  public async orchestrateRecommendations(
    userId: string,
    forcePersonalized: boolean = false
  ): Promise<{ personalized: Recommendation[], popular: Recommendation[] }> {
    try {
      if (!userId) {
        console.error('[Orchestrator] ID de usuário inválido');
        return { personalized: [], popular: [] };
      }
      
      const allFeedbackGameIds = await this.userInteractionDataService.getAllFeedbackGameIds(userId);
      console.log(`[Orchestrator] Obtidos ${allFeedbackGameIds.length} IDs de jogos com feedback para excluir (via UserInteractionDataService).`);
      
      const otherExcludeGameIds = await this.userInteractionDataService.getAllInteractedGameIds(userId);
      console.log(`[Orchestrator] Obtidos ${otherExcludeGameIds.length} IDs de jogos interagidos (via UserInteractionDataService).`);
      
      const excludeGameIds = [...new Set([...allFeedbackGameIds, ...otherExcludeGameIds])];
      console.log(`[Orchestrator] Excluindo total de ${excludeGameIds.length} jogos.`);
      
      const cacheKey = `recommendations:${userId}:${forcePersonalized ? 'force' : 'default'}`;
      let cachedRecommendations = null;
      if (!forcePersonalized) {
        cachedRecommendations = await this.cacheService.get(cacheKey);
      }
      
      if (cachedRecommendations) {
        console.log(`[OrchestratorCache] Cache hit for user ${userId}. Filtrando.`);
        const filteredCached = cachedRecommendations.filter(
          (rec: Recommendation) => !allFeedbackGameIds.includes(rec.game_id)
        );
        if (filteredCached.length < cachedRecommendations.length) {
          console.log(`[OrchestratorCache] Removidas ${cachedRecommendations.length - filteredCached.length} recomendações com feedback.`);
        }
        const personalized = filteredCached.filter((rec: Recommendation) => rec.algorithm !== RecommendationAlgorithm.POPULAR);
        const popular = filteredCached.filter((rec: Recommendation) => rec.algorithm === RecommendationAlgorithm.POPULAR);
        
        if (personalized.length > 0 || popular.length > 0) {
          console.log(`[OrchestratorCache] Retornando ${personalized.length}p e ${popular.length}pop do cache.`);
          return {
            personalized: personalized.slice(0, 3),
            popular: popular.slice(0, 3)
          };
        } else {
          console.log(`[OrchestratorCache] Cache hit, mas sem recomendações válidas. Gerando novas.`);
        }
      } else {
        console.log(`[OrchestratorCache] Cache miss for user ${userId}. Gerando recomendações.`);
      }
      
      const request: RecommendationRequest = {
        userId,
        limit: 3,
        dislikedGameIds: excludeGameIds,
        forcePersonalized: forcePersonalized
      };
      
      console.log(`[Orchestrator] Gerando personalizadas para ${userId} (forcePersonalized=${forcePersonalized})...`);
      const personalizedRecommendations = await this.adaptiveRecommender.generateRecommendations(request);
      console.log(`[Orchestrator] Geradas ${personalizedRecommendations.length} personalizadas.`);
      
      const idsToExcludeForPopular = [...excludeGameIds, ...personalizedRecommendations.map(rec => rec.game_id)];
      console.log(`[Orchestrator] Gerando populares para ${userId}, excluindo ${idsToExcludeForPopular.length} IDs...`);
      
      // Double check feedback IDs before popular
      const currentFeedbackIds = await this.userInteractionDataService.getAllFeedbackGameIds(userId);
      const allExcludeIdsForPopular = [...new Set([...idsToExcludeForPopular, ...currentFeedbackIds])];
      
      const popularRecommendations = await this.popularRecommendationService.getPopularRecommendations(userId, 3, allExcludeIdsForPopular);
      console.log(`[Orchestrator] Geradas ${popularRecommendations.length} populares.`);
      
      const allRecs = [...personalizedRecommendations, ...popularRecommendations];
      
      const finalFeedbackIds = await this.userInteractionDataService.getAllFeedbackGameIds(userId); // Final check
      const filteredRecs = allRecs.filter(rec => !finalFeedbackIds.includes(rec.game_id));
      
      if (filteredRecs.length < allRecs.length) {
        console.log(`[Orchestrator] Removidas ${allRecs.length - filteredRecs.length} recomendações com feedback após geração.`);
      }
      
      if (filteredRecs.length > 0) {
        console.log(`[Orchestrator] Salvando ${filteredRecs.length} recomendações no DB.`);
        await this.storage.saveRecommendations(filteredRecs);
        console.log('[Orchestrator] Recomendações salvas no DB.');
        
        console.log(`[OrchestratorCache] Setando cache para ${userId}.`);
        await this.cacheService.set(cacheKey, filteredRecs, this.cacheExpirationTime);
        
        console.log(`[Orchestrator] Rastreando impressões para ${filteredRecs.length} recomendações.`);
        this.monitor.trackImpressions(filteredRecs as any[]);
      } else {
        console.log("[Orchestrator] Nenhuma recomendação gerada, pulando salvamento e cache.");
      }
      
      const finalPersonalized = filteredRecs.filter(rec => rec.algorithm !== RecommendationAlgorithm.POPULAR);
      const finalPopular = filteredRecs.filter(rec => rec.algorithm === RecommendationAlgorithm.POPULAR);
      
      return {
        personalized: finalPersonalized,
        popular: finalPopular
      };
    } catch (error) {
      console.error('[Orchestrator] Erro ao obter recomendações:', error);
      return { personalized: [], popular: [] };
    }
  }
} 